import React, { ErrorInfo, ReactNode } from 'react';
import { ErrorBoundaryProps } from '@sentry/react';
import { Button, Container } from '@mui/material';
import { t } from 'i18next';
import { captureException } from './sentry';
import NotFoundImage from '../assets/img/img_error404.png';
import { Logger } from '../core/logger/Logger';
import './ErrorBoundary.scss';

type State = {
  hasError: boolean;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: ErrorBoundaryProps): State {
    Logger.error(error);
    captureException(error);
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(errorInfo);
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <Container className='error-boundary'>
          <img
            className='error-boundary__image'
            src={NotFoundImage}
            alt={t('errors.boundary.imageAlt', 'Fehler')}
          />
          <h1 className='error-boundary__title'>
            {t('errors.boundary.title', 'Hoppla, da ist etwas schief gelaufen')}
          </h1>
          <p>
            {t(
              'errors.boundary.message',
              'Oh-oh! Sieht so aus als wäre etwas schief gelaufen. Bitte versuche es noch einmal.'
            )}
          </p>
          {/* A plain link: this boundary sits outside the router, and a fresh
              page load is the way out of a crashed one anyway. */}
          <Button
            href='/'
            variant='contained'
            color='primary'
            size='large'
            className='error-boundary__action'>
            {t('errors.boundary.backToHome', 'ZUR STARTSEITE')}
          </Button>
        </Container>
      );
    }

    return children;
  }
}
